import { launched } from '@spotify-internal/market';
import { useTranslation } from 'react-i18next';
import { FunctionComponent } from 'react';

import SelectField from 'components/Fields/SelectField/SelectField';
import { USE_LABEL_FROM_TRANSLATION_PREFIX } from 'components/ContentReporting/shared-constants';
import { withUserInfo } from 'context/UserInfo/UserInfoProvider';
import { FieldChangeData, UserInfoProps } from 'etc/types';

export const withFullTranslationKey = (translationKey?: string): string => `user_country:${translationKey}`;

interface UserCountryFieldProps extends UserInfoProps {
  forceValidation: boolean;
}

let userCountryIsValid = false;

const isoToMarket = (iso: string) => {
  const marketList = [
    'ad',
    'ar',
    'at',
    'au',
    'be-fr',
    'be-nl',
    'bg',
    'bo',
    'br',
    'ca-en',
    'ca-fr',
    'ch-de',
    'ch-fr',
    'cl',
    'co',
    'cr',
    'cy',
    'cz',
    'de',
    'dk',
    'do',
    'ec',
    'ee',
    'es',
    'fi',
    'fr',
    'gr',
    'gt',
    'hk-en',
    'hk-zh',
    'hn',
    'hu',
    'id',
    'ie',
    'is',
    'it',
    'jp',
    'li',
    'lt',
    'lu-de',
    'lu-fr',
    'lv',
    'mc',
    'mt',
    'mx',
    'my-en',
    'my-ms',
    'ni',
    'nl',
    'no',
    'nz',
    'pa',
    'pe',
    'ph',
    'pl',
    'pt',
    'py',
    'se',
    'sg-en',
    'sk',
    'sv',
    'tr',
    'tw',
    'uk',
    'us',
    'uy',
  ];

  if (iso === 'GB') {
    return 'uk';
  }
  return marketList.filter(market => market.startsWith(iso.toLowerCase()))[0];
};

const marketToISO = (market: string | undefined) => {
  if (market === undefined) return '';
  if (market === 'uk') {
    return 'GB';
  }
  return market.toUpperCase().slice(0, 2);
};

const UserCountryField: FunctionComponent<UserCountryFieldProps> = props => {
  const { t } = useTranslation('user_country');
  const { forceValidation, userInfo } = props;

  const onFieldChange = ({ value, valid }: FieldChangeData) => {
    if (valid) {
      userCountryIsValid = true;
      userInfo.setUserProvidedCountry(isoToMarket(value));
    }
  };

  userCountryIsValid = !!userInfo.data?.country;

  return (
    <>
      {userInfo.isUserCountryError && (
        <SelectField
          field={{
            name: 'userCountry',
            type: 'text',
            options: launched,
            required: userInfo.isUserCountryError,
            labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
          }}
          hasDefaultPlaceholder
          tPrefix={withFullTranslationKey('user_country')}
          value={marketToISO(userInfo.data?.country)}
          valid={!!userInfo.data?.country}
          errorMessageKey={withFullTranslationKey('field_required')}
          onFieldChange={onFieldChange}
          forceValidation={forceValidation}
          t={t}
        />
      )}
    </>
  );
};

export default withUserInfo(UserCountryField);
export { userCountryIsValid };
